/* ===========================================================
   OG90s Cursor Engine v1.0
   =========================================================== */

(() => {
  "use strict";

  const BLADE = {
    length: 30,
    baseWidth: 5,
    midWidth: 4,
    tipWidth: 0.3,
    tipLength: 16
  };

  const CONFIG = {
    smoothing: 0.35,
    mobileBreakpoint: 768
  };

  const COLORS = {
    red: "#D71920",
    darkRed: "#8B0E14",
    glow: "rgba(215,25,32,.55)",
    glowSoft: "rgba(215,25,32,.18)",
    white: "#FFFFFF",
    silver: "#FFEAEA"
  };

  // Only disable if device is purely touch with NO fine mouse pointer
  const isTouchOnly = !window.matchMedia("(pointer: fine)").matches && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
  if (isTouchOnly || window.innerWidth <= CONFIG.mobileBreakpoint) {
    return;
  }

  /* Canvas Setup */
  const cursorCanvas = document.getElementById("og90s-cursor");
  const glowCanvas = document.getElementById("og90s-cursor-glow");
  const trailCanvas = document.getElementById("og90s-cursor-trail");

  if (!cursorCanvas || !glowCanvas || !trailCanvas) return;

  const ctx = cursorCanvas.getContext("2d");
  const glowCtx = glowCanvas.getContext("2d");
  const trailCtx = trailCanvas.getContext("2d");

  let width = window.innerWidth;
  let height = window.innerHeight;
  let dpr = 1;

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = window.innerWidth;
    height = window.innerHeight;

    [cursorCanvas, glowCanvas, trailCanvas].forEach((canvas) => {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
    });

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    glowCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    trailCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  resize();
  window.addEventListener("resize", resize);

  /* Mouse tracking */
  const mouse = {
    x: width / 2,
    y: height / 2,
    tx: width / 2,
    ty: height / 2,
    vx: 0,
    vy: 0,
    angle: 0
  };

  window.addEventListener("mousemove", (e) => {
    mouse.tx = e.clientX;
    mouse.ty = e.clientY;
    document.body.classList.add("cursor-visible");
  });

  /* Hover states */
  window.addEventListener("mouseover", (e) => {
    if (e.target.closest("a, button, [role='button'], input, select, textarea")) {
      document.body.classList.add("cursor-hover");
    }
  });

  window.addEventListener("mouseout", (e) => {
    if (e.target.closest("a, button, [role='button'], input, select, textarea")) {
      document.body.classList.remove("cursor-hover");
    }
  });

  /* Particles & Slashes */
  const particles = [];
  const MAX_PARTICLES = 250;
  const slashes = [];
  const MAX_SLASHES = 10;

  window.addEventListener("mousedown", () => {
    slashes.push({
      x: mouse.x,
      y: mouse.y,
      angle: mouse.angle,
      length: 0,
      alpha: 1
    });

    if (slashes.length > MAX_SLASHES) slashes.shift();
  });

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function update() {
    const prevX = mouse.x;
    const prevY = mouse.y;

    mouse.x = lerp(mouse.x, mouse.tx, CONFIG.smoothing);
    mouse.y = lerp(mouse.y, mouse.ty, CONFIG.smoothing);

    mouse.vx = mouse.x - prevX;
    mouse.vy = mouse.y - prevY;

    if (Math.hypot(mouse.vx, mouse.vy) > 0.5) {
      mouse.angle = Math.atan2(mouse.vy, mouse.vx);
    }
  }

  function clear() {
    ctx.clearRect(0, 0, width, height);
    glowCtx.clearRect(0, 0, width, height);
    trailCtx.clearRect(0, 0, width, height);
  }

  /* Draw Custom Blade Cursor */
  function draw() {
    ctx.save();
    ctx.translate(mouse.x, mouse.y);
    ctx.rotate(mouse.angle);

    ctx.shadowColor = COLORS.red;
    ctx.shadowBlur = 6;

    const bladeGradient = ctx.createLinearGradient(-BLADE.length, 0, 40, 0);
    bladeGradient.addColorStop(0.0, COLORS.white);
    bladeGradient.addColorStop(0.2, COLORS.silver);
    bladeGradient.addColorStop(0.6, COLORS.red);
    bladeGradient.addColorStop(1.0, COLORS.darkRed);

    ctx.fillStyle = bladeGradient;

    const L = BLADE.length;
    const BW = BLADE.baseWidth;
    const MW = BLADE.midWidth;
    const TL = BLADE.tipLength;

    ctx.beginPath();
    ctx.moveTo(-L, -BW / 2);
    ctx.lineTo(15, -BW / 2);
    ctx.lineTo(30, -MW / 2);
    ctx.lineTo(30 + TL, 0);
    ctx.lineTo(30, MW / 2);
    ctx.lineTo(15, BW / 2);
    ctx.lineTo(-L, BW / 2);
    ctx.closePath();
    ctx.fill();

    // Cutting edge
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.moveTo(-L * 0.8, -1.2);
    ctx.lineTo(15, -1.2);
    ctx.lineTo(40, -0.2);
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Energy Core Center
    ctx.beginPath();
    ctx.arc(0, 0, 2, 0, Math.PI * 2);
    ctx.fillStyle = COLORS.white;
    ctx.fill();

    ctx.restore();
  }

  /* Draw Glow */
  function drawGlow() {
    const speed = Math.hypot(mouse.vx, mouse.vy);
    const hover = document.body.classList.contains("cursor-hover");
    const outer = hover ? 50 : 35;

    const gradient = glowCtx.createRadialGradient(
      mouse.x,
      mouse.y,
      2,
      mouse.x,
      mouse.y,
      outer + speed * 2
    );

    gradient.addColorStop(0, "rgba(255,255,255,0.25)");
    gradient.addColorStop(0.2, COLORS.glow);
    gradient.addColorStop(0.6, COLORS.glowSoft);
    gradient.addColorStop(1, "rgba(0,0,0,0)");

    glowCtx.save();
    glowCtx.beginPath();
    glowCtx.arc(mouse.x, mouse.y, outer + speed, 0, Math.PI * 2);
    glowCtx.fillStyle = gradient;
    glowCtx.fill();
    glowCtx.restore();
  }

  /* Particles */
  function createParticle() {
    const speed = Math.hypot(mouse.vx, mouse.vy);
    if (speed < 0.8) return;

    particles.push({
      x: mouse.x - Math.cos(mouse.angle) * 15,
      y: mouse.y - Math.sin(mouse.angle) * 15,
      vx: -mouse.vx * 0.15 + (Math.random() - 0.5) * 0.6,
      vy: -mouse.vy * 0.15 + (Math.random() - 0.5) * 0.6,
      size: 5 + Math.random() * 4,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.2,
      life: 1,
      color: Math.random() > 0.4 ? COLORS.white : COLORS.red
    });

    if (particles.length > MAX_PARTICLES) particles.shift();
  }

  function drawParticles() {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.rotationSpeed;
      p.life -= 0.025;
      p.size *= 0.985;

      if (p.life <= 0 || p.size < 0.3) {
        particles.splice(i, 1);
        continue;
      }

      trailCtx.save();
      trailCtx.translate(p.x, p.y);
      trailCtx.rotate(p.rotation);
      trailCtx.globalAlpha = p.life * 0.8;
      trailCtx.shadowColor = COLORS.red;
      trailCtx.shadowBlur = 10;

      trailCtx.beginPath();
      trailCtx.arc(0, 0, p.size, Math.PI * 0.35, Math.PI * 1.65);
      trailCtx.lineWidth = 1.8;
      trailCtx.strokeStyle = p.color;
      trailCtx.stroke();

      trailCtx.restore();
    }
  }

  /* Draw Slashes on Click */
  function drawSlashes() {
    for (let i = slashes.length - 1; i >= 0; i--) {
      const s = slashes[i];
      s.length += 25;
      s.alpha *= 0.9;

      if (s.alpha < 0.03) {
        slashes.splice(i, 1);
        continue;
      }

      glowCtx.save();
      glowCtx.translate(s.x, s.y);
      glowCtx.rotate(s.angle);
      glowCtx.globalAlpha = s.alpha;
      glowCtx.shadowColor = COLORS.red;
      glowCtx.shadowBlur = 20;

      const gradient = glowCtx.createLinearGradient(-s.length, 0, s.length, 0);
      gradient.addColorStop(0, "rgba(0,0,0,0)");
      gradient.addColorStop(0.3, COLORS.darkRed);
      gradient.addColorStop(0.5, COLORS.white);
      gradient.addColorStop(0.7, COLORS.red);
      gradient.addColorStop(1, "rgba(0,0,0,0)");

      glowCtx.strokeStyle = gradient;
      glowCtx.lineWidth = 3;
      glowCtx.beginPath();
      glowCtx.moveTo(-s.length, 0);
      glowCtx.lineTo(s.length, 0);
      glowCtx.stroke();

      glowCtx.restore();
    }
  }

  /* Main Loop */
  function animate() {
    update();

    const speed = Math.hypot(mouse.vx, mouse.vy);
    const count = Math.max(1, Math.min(5, Math.floor(speed / 3)));
    for (let i = 0; i < count; i++) {
      createParticle();
    }

    clear();
    drawParticles();
    drawGlow();
    drawSlashes();
    draw();

    requestAnimationFrame(animate);
  }

  animate();
})();